import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { z } from "zod";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectField,
  SelectFieldWithSection,
  TextareaField,
  TSections,
} from "@/features/FormCard/Fields";

import {
  dateSchema,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { FixingMode } from "@/data/fixingMode";
import { useEditForm } from "@/features/Edit/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useEffect, useMemo, useRef } from "react";
import { useCategoryAutoSelection, useFeesCalculator } from "./hook";
import { fieldTranslations } from "./translations";
import {
  deriveLocalCurrency,
  getKeyByTranslation,
  isCashAccount,
  isCashFormat,
  isSecuritiesAccount,
} from "./utils";
import { AUTOMATIC_TAG_MAP } from "./config";
import { computeFixingMode } from "../A9TimeSeries/utils";
import { CashFeeContent, SecuritiesFeeContent } from "./fees_edit";
import { FormCleaner } from "@/hooks/useCleanWhenHidden";
import i18n from "@/i18n";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),

  a9_portfolio_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().nullable().optional(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string(),
        currency: z.string(),
      }),
    ]),
  ),
  a9_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().nullable().optional(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        asset_class_2: z.string(),
        asset_class_1: z.string(),
        currency: z.string(),
        reporting_currency: z.string(),
      }),
    ]),
  ),
  a9_portfolio_reporting_currency: z.string().nullable().optional(),
  a9_portfolio_account_type: z.string().nullable().optional(),
  a9_static_datum_asset_class_1: z.string().nullable().optional(),
  a9_static_datum_asset_class_2: z.string().nullable().optional(),
  a9_portfolio_id: z.number(),
  a9_static_datum_id: z.number(),
  quantity: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number(),
  category: z.string().nullable().optional(),
  transfer: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  fee_currency: z.string().nullable().optional(),
  broker_fee: z.coerce.number().nullable().optional(),
  custodian_broker_fee: z.coerce.number().nullable().optional(),
  other_fee: z.coerce.number().nullable().optional(),
  fees: z.coerce.number().nullable().optional(),
  fee_type: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
  a9_portfolio_currency: z.string().nullable().optional(),
  a9_static_datum_currency: z.string().nullable().optional(),
});

type BaseForm = z.infer<typeof FormSchema>;

export type TForm = Omit<
  BaseForm,
  "a9_portfolio_name" | "a9_static_datum_name"
> & {
  a9_portfolio_name: Extract<BaseForm["a9_portfolio_name"], { id: number }>;
  a9_static_datum_name: Extract<
    BaseForm["a9_static_datum_name"],
    { id: number }
  >;
};

export function A9TransactionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
    translations,
    isLoadingData,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    fieldsNotApplyTranslation: ["transfer", "fee_type"],
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "a9_portfolio_name") {
        return {
          text: value,
          id: fetchedData.a9_portfolio_id,
          account_type: fetchedData.a9_portfolio_account_type,
          currency: fetchedData.a9_portfolio_currency,
          reporting_currency: fetchedData.a9_portfolio_reporting_currency,
        };
      } else if (key === "a9_static_datum_name") {
        return {
          text: value,
          id: fetchedData.a9_static_datum_id,
          asset_class_2: fetchedData.a9_static_datum_asset_class_2,
          asset_class_1: fetchedData.a9_static_datum_asset_class_1,
          currency: fetchedData.a9_static_datum_currency,
        };
      }
      if (key === "category") {
        return fetchedData.category;
      }
      if (key === "fee_type") {
        return fetchedData.fee_type;
      }
      if (key === "transfer") {
        return fetchedData.transfer;
      }
      return value;
    },
  });

  const currencyOptions = useCurrencyOptions();

  const { category_options, isCategoryDisabled } = useCategoryAutoSelection({
    form,
    translations,
    keysAllowNullValue: ["fees"],
    isLoadingData,
  });

  const calculateTotalFees = useFeesCalculator(form);

  const portfolio = form.watch("a9_portfolio_name");
  const portfolioId = portfolio?.id;
  const portfolioType = portfolio?.account_type;
  const portfolioCurrency = portfolio?.currency;
  const reportingCurrency = portfolio?.reporting_currency;

  const asset = form.watch("a9_static_datum_name");
  const assetId = asset?.id;
  const assetCurrency = asset?.currency;
  const assetClass2 = asset?.asset_class_2 ?? null;

  const client_name = form.getValues("client")?.text ?? "";
  const bank_name = form.getValues("bank_name")?.text ?? "";

  const category = form.watch("category");
  const marketValueLocal = form.watch("market_value_local_ccy");
  const transferAutoRef = useRef(false);

  const localCurrency = useMemo(
    () =>
      deriveLocalCurrency(
        portfolioType ?? null,
        portfolioCurrency,
        assetCurrency,
      ),
    [portfolioType, portfolioCurrency, assetCurrency],
  );

  const isTypeCash = isCashAccount(portfolioType);
  const isTypeSecurities = isSecuritiesAccount(portfolioType);

  const isCategoryFees =
    getKeyByTranslation(category, translations?.["category"]) === "fees";
  const isCategoriesEmpty = category_options.options.length == 0;

  const isCashAsset = useMemo(() => isCashFormat(asset?.text), [asset]);
  const fixingMode: FixingMode | null = useMemo(
    () => computeFixingMode(assetClass2, asset?.text),
    [assetClass2, asset?.text],
  );

  const changesArmedRef = useRef(false);
  useEffect(() => {
    const sub = form.watch((_values, info) => {
      // arm on any meaningful change
      if (
        info?.name === "a9_static_datum_name" ||
        info?.name === "a9_portfolio_name" ||
        info?.name === "market_value_local_ccy"
      ) {
        changesArmedRef.current = true;
      }
    });
    return () => sub?.unsubscribe?.();
  }, [form]);

  const hadInitialQuantityRef = useRef(
    form.getValues("quantity") !== null &&
      form.getValues("quantity") !== undefined,
  );
  const isShowQuantity =
    !asset ||
    !fixingMode ||
    (isTypeSecurities && fixingMode === FixingMode.FIXING);
  const showQuantityField = changesArmedRef.current
    ? isShowQuantity
    : hadInitialQuantityRef.current || isShowQuantity;

  const amountSign = useMemo(() => {
    const n = Number(marketValueLocal);
    return isNaN(n) ? 0 : n === 0 ? 0 : n > 0 ? 1 : -1;
  }, [marketValueLocal]);

  useEffect(() => {
    if (!AUTOMATIC_TAG_MAP.includes(assetClass2)) {
      if (transferAutoRef.current) {
        form.setValue("transfer", null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // compute the desired label from the sign
    const nextLabel =
      amountSign > 0 ? "deposit" : amountSign < 0 ? "withdrawal" : "";
    const current = form.getValues("transfer");

    // no label when amount is 0/blank: clear only if we set it
    if (!nextLabel) {
      if (transferAutoRef.current) {
        form.setValue("transfer", null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // update whenever the derived label differs
    if (current !== nextLabel) {
      form.setValue("transfer", nextLabel, { shouldDirty: true });
      transferAutoRef.current = true; // mark as auto-set
    }
  }, [assetClass2, marketValueLocal, form]);

  // Clear quantity when it becomes hidden (prevents stale values)
  useEffect(() => {
    if (!showQuantityField) {
      form.setValue("quantity", null, { shouldDirty: true });
    }
  }, [asset, showQuantityField]);

  const prevPidRef = useRef<number | string | null>(null);
  useEffect(() => {
    const pid = portfolioId ?? null;
    const prev = prevPidRef.current;

    if (prev && !pid) {
      form.resetField("client", { defaultValue: null });
      form.resetField("bank_name", { defaultValue: null });
    }

    prevPidRef.current = pid;
  }, [portfolioId, form]);

  const isFeeTypeEnabled = isTypeCash && isCashAsset && isCategoryFees;

  const ready =
    !isLoadingData && !!translations && category_options.options != null;

  const cleanSpecs = useMemo(
    () => [
      { name: "category" as const, isVisible: !isCategoriesEmpty },
      { name: "quantity" as const, isVisible: showQuantityField },
      { name: "fee_type" as const, isVisible: isFeeTypeEnabled },
      { name: "fee_currency" as const, isVisible: isTypeSecurities },
      { name: "broker_fee" as const, isVisible: isTypeSecurities },
      { name: "custodian_broker_fee" as const, isVisible: isTypeSecurities },
      { name: "other_fee" as const, isVisible: isTypeSecurities },
      { name: "fees" as const, isVisible: isTypeSecurities },
    ],
    [isCategoriesEmpty, showQuantityField, isFeeTypeEnabled, isTypeSecurities],
  );

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <FormCleaner specs={cleanSpecs} ready={ready} />
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("transactions.transaction")} {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("transactions.transaction_date")}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name="a9_static_datum_name"
                  label={t("transactions.asset")}
                  isRequired={true}
                  control={form.control}
                  base_url="a9/a9_static_data"
                  id_column="a9_static_datum_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: "a9",
                    table_name: "a9_static_data",
                    language: i18n.resolvedLanguage?.startsWith("fr")
                      ? "fr"
                      : "en",
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    key={`client-${portfolioId ?? "none"}}`}
                    name="client"
                    label={t("transactions.client")}
                    control={form.control}
                    base_url="a9/a9_portfolios"
                    id_column="a9_portfolio_id"
                    column_name="client"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: portfolioId ? true : false,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (bank_name) equals.bank_name = bank_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                  <AutocompleteAsyncField
                    key={`bank-${portfolioId ?? "none"}`}
                    name="bank_name"
                    label={t("transactions.bank")}
                    control={form.control}
                    base_url="a9/a9_portfolios"
                    id_column="a9_portfolio_id"
                    column_name="bank_name"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: portfolioId ? true : false,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (client_name) equals.client = client_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                </div>
                <AutocompleteAsyncField
                  name="a9_portfolio_name"
                  label={t("transactions.account")}
                  isRequired={true}
                  control={form.control}
                  base_url="a9/a9_portfolios"
                  id_column="a9_portfolio_id"
                  column_name="name"
                  options={{
                    filters: () => {
                      const equals: Record<string, string> = {};
                      if (client_name) equals.client = client_name;
                      if (portfolioId) equals.id = portfolioId;
                      return { equals };
                    },
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    isRequired={true}
                    name="market_value_local_ccy"
                    label={t("transactions.amount_local")}
                    type="number"
                    control={form.control}
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t("transactions.amount_reporting")}
                    type="number"
                    control={form.control}
                    endContent={
                      <div className="opacity-50 text-xs">
                        {reportingCurrency}
                      </div>
                    }
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="quantity"
                    label={t("transactions.quantity")}
                    control={form.control}
                    type="number"
                    isRequired={asset && showQuantityField}
                    fieldOptions={{
                      isDisabled: isTypeCash || !showQuantityField,
                      changeOnDisable: false,
                    }}
                  />
                  <SelectField
                    name="transfer"
                    label={t("transactions.transfer_type")}
                    options={translations?.["transfer"]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldWithSection
                    allow_empty_option={isCashAsset}
                    label={t("transactions.movement_type")}
                    name="category"
                    sections={category_options.options as TSections}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: isCategoryDisabled,
                      isVisible: !isCategoriesEmpty,
                    }}
                    placeholder={t("form.placeholders.select")}
                    isRequired={isTypeSecurities && !isCategoriesEmpty}
                  />
                </div>
                <div className="overflow-hidden">
                  {isTypeCash && isCashAsset && (
                    <CashFeeContent
                      form={form}
                      isDisabled={!isFeeTypeEnabled}
                      feeOptionsTranslation={translations?.["fee_type"]}
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t("transactions.movement_description")}
                    control={form.control}
                  />
                </div>
                <div className="max-w-full bg-transparent text-xs">
                  {isTypeSecurities && (
                    <div className="py-2">
                      <Divider />
                      <SecuritiesFeeContent
                        form={form}
                        currencyOptions={currencyOptions}
                        calculateTotalFees={calculateTotalFees}
                      />
                    </div>
                  )}
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
          {assetId && (
            <Button
              href={toWorkspacePath(`/admin/a9/a9_static_data/${assetId}`)}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Actif
            </Button>
          )}
          {portfolioId && (
            <Button
              href={toWorkspacePath(`/admin/a9/a9_portfolios/${portfolioId}`)}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Compte
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(
                  changedFields,
                  ["a9_portfolio_id", "a9_static_datum_id"],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
